import React, {useEffect, useState} from 'react';
import {Button, Form, Grid, Header, Icon, Input, Label, Pagination, Segment, Table} from 'semantic-ui-react';
import {useNavigate} from "react-router-dom";
import moment from "moment/moment";
import {toast} from "react-toastify";
import {SessionUser} from "../../../models/session-user";
import SessionStorage from "../../../store/session";
import {MotoristaFilter} from "../../../filters/motorista-filter";
import {useBlockUI} from "../../../shared/services/block-ui-service";
import StringUtils from "../../../shared/utils/string-utils";
import {ToasterConfig} from "../../../config/ToasterConfig";
import useDriverService from "../../../services/backoffice/hooks/useDriverService";
import useFleetService from "../../../services/backoffice/hooks/useFleetService";
import {Driver} from "../../../models/driver";
import {ShortDto} from "../../../models/short-dto";
import TooltipInfo from "../../../components/tooltip/tooltip";
import DriverProgrammingsModal from "../../../components/programming-list-page/DriverProgrammingsModal";

const HAS_PLATE_OPTIONS = [
    {key: 'all', text: 'Todas', value: ''},
    {key: 'with', text: 'Com placa', value: 'true'},
    {key: 'without', text: 'Sem placa', value: 'false'},
];

const TruckDriversList = () => {
    const [motoristas, setMotoristas] = useState<Driver[]>([]);
    const [error, setError] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const [filter, setFilter] = useState<MotoristaFilter>()
    const [totalPages, setTotalPages] = useState<number>()
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const [fleets, setFleets] = useState<ShortDto[]>([]);
    const {getMotoristas, activateMotorista, deactiveMotorista, deleteMotorista, resetMotoristaPassword} = useDriverService();
    const {getAllFleetsShortDto} = useFleetService();
    const blockUI = useBlockUI()
    const [showFilters, setShowFilters] = useState(false);
    const [programmingsModalOpen, setProgrammingsModalOpen] = useState(false);
    const [selectedDriverForProgrammings, setSelectedDriverForProgrammings] = useState<Driver | null>(null);

    useEffect(() => {
        blockUI?.setActive(loading)
    }, [loading]);

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user)
        }

        updateSessionUser()
    }, [])

    useEffect(() => {
        getAllFleetsShortDto()
            .then(setFleets)
            .catch(() => {});
    }, []);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };
    useEffect(() => {
        listMotoristas();
    }, [filter?.Page]);

    async function listMotoristas(overrideFilter?: MotoristaFilter) {
        setLoading(true)
        const response = (await getMotoristas(overrideFilter ?? filter).finally(() => setLoading(false)));
        setTotalPages(response.totalPages);
        setMotoristas(response.itens);
    }

    function applyFilters() {
        const nextFilter: MotoristaFilter = {...filter, Page: 1};
        setFilter(nextFilter);
        listMotoristas(nextFilter);
    }

    const hasPlateFilterValue = filter?.HasVehiclePlate === true
        ? 'true'
        : filter?.HasVehiclePlate === false
            ? 'false'
            : '';

    const toggleActivationTruckDriver = async (motorista: any, activate?: boolean) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${motorista.isActive ? 'desativar' : 'ativar'} o motorista ${motorista.firstName} ${motorista.lastName}?`);
            if (!result) return
            setLoading(true);
            if (motorista) {
                const result = !motorista.isActive ? await activateMotorista(motorista.id) : await deactiveMotorista(motorista.id);
                if (!!result) {
                    toast.success((`O Motorista foi ${motorista.isActive ? 'desativado' : 'ativado'} com sucesso`), ToasterConfig.success);
                    setError('');
                    listMotoristas();
                }
            }
            setLoading(false);
        } catch (error: any) {
            toast.warn(`Falha ao ${motorista.isActive ? 'desativar' : 'ativar'} motorista`, ToasterConfig.error);
            setLoading(false);
            setError(error.message);
        }
    };

    const handleCopyPassword = (password: string) => {
        navigator.clipboard.writeText(password);
        toast.info('Senha copiada para a área de transferência');
    };

    async function resetMotoristaPasswordFunction(motorista: Driver) {
        const confirm = window.confirm(
            `Redefinir a senha de ${motorista.firstName} para os 6 primeiros dígitos do CPF?`
        );
        if (!confirm) return;

        try {
            setLoading(true);
            const result = await resetMotoristaPassword(motorista.id);
            toast.success(
                <div>
                    {result.message}
                    {result.password && (
                        <button type="button" onClick={() => handleCopyPassword(result.password!)}>
                            Copiar senha
                        </button>
                    )}
                </div>,
                {autoClose: false}
            );
        } catch (error: any) {
            const message = error?.response?.data?.errors?.[0]
                ?? error?.response?.data?.message
                ?? 'Falha ao redefinir senha do motorista';
            toast.warn(message, ToasterConfig.error);
        } finally {
            setLoading(false);
        }
    }

    async function deleteMotoristaFunction(motorista: any) {
        try {
            const confirm = window.confirm(`Tem certeza que deseja apagar o motorista ${motorista.firstName} ${motorista.lastName}?`);

            if (confirm) {
                if (motorista) {
                    await deleteMotorista(motorista.id);
                    toast.success('Motorista deletado com sucesso', ToasterConfig.success);
                    listMotoristas();
                }
                setLoading(false);
            }
        } catch (error: any) {
            toast.warn('Falha ao deletar motorista', ToasterConfig.error);
            setLoading(false);
            setError(error.message);
        }
    }

    return (
        <div>
            <Header as="h3" textAlign="center" color={'green'}>
                Motoristas
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {(sessionUser?.userData?.profile == 'Admin'
                        || sessionUser?.userData?.profile == 'Manager') &&
                        <Button color="green"
                                onClick={() => navigate('/backoffice/motoristas/create/')}>

                            <Icon name='plus'/> Novo
                        </Button>}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color={"green"}>
                        <Icon name={showFilters ? 'hide' : 'unhide'}/>
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>
            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={4}>
                                <label htmlFor="userName">Nome do usuário</label>
                                <Input
                                    fluid
                                    id="userName"
                                    icon="search"
                                    type="text"
                                    placeholder="Nome do usuário"
                                    value={filter?.Name ?? ''}
                                    onChange={event => setFilter(prev => ({...prev, Name: event.target.value}))}
                                />
                            </Grid.Column>
                            <Grid.Column width={3}>
                                <label htmlFor="cpf">CPF</label>
                                <Input
                                    fluid
                                    id="cpf"
                                    icon="search"
                                    type="number"
                                    placeholder="CPF"
                                    value={filter?.Cpf ?? ''}
                                    onChange={event => setFilter(prev => ({...prev, Cpf: event.target.value}))}
                                />
                            </Grid.Column>
                            <Grid.Column width={3}>
                                <label htmlFor="plate">Placa</label>
                                <Input
                                    fluid
                                    id="plate"
                                    icon="search"
                                    type="text"
                                    placeholder="Placa"
                                    value={filter?.Plate ?? ''}
                                    onChange={event => setFilter(prev => ({...prev, Plate: event.target.value}))}
                                />
                            </Grid.Column>
                            <Grid.Column width={3}>
                                <label htmlFor="hasPlate">Vínculo com placa</label>
                                <Form.Select
                                    id="hasPlate"
                                    fluid
                                    options={HAS_PLATE_OPTIONS}
                                    placeholder="Todas"
                                    value={hasPlateFilterValue}
                                    onChange={(_e, data) => {
                                        const selected = data.value as string;
                                        setFilter(prev => ({
                                            ...prev,
                                            HasVehiclePlate: selected === ''
                                                ? undefined
                                                : selected === 'true',
                                        }));
                                    }}
                                />
                            </Grid.Column>
                            <Grid.Column width={3}>
                                <label htmlFor="fleetId">Frota (operação ativa)</label>
                                <Form.Select
                                    id="fleetId"
                                    fluid
                                    options={fleets}
                                    placeholder="Todas as frotas"
                                    value={filter?.FleetId ?? ''}
                                    search
                                    clearable
                                    onChange={(_e, data) => {
                                        setFilter(prev => ({
                                            ...prev,
                                            FleetId: (data.value as string) || undefined,
                                        }));
                                    }}
                                />
                            </Grid.Column>
                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color={"green"} onClick={applyFilters}>Filtrar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}


            <Table color={"green"}>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Nome</Table.HeaderCell>
                        <Table.HeaderCell>CPF</Table.HeaderCell>
                        <Table.HeaderCell>Cliente</Table.HeaderCell>
                        <Table.HeaderCell>Operação At.</Table.HeaderCell>
                        <Table.HeaderCell>Placa At.</Table.HeaderCell>
                        <Table.HeaderCell>Criado em</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>

                {(sessionUser?.userData?.profile == 'Admin'  || sessionUser?.userData?.profile == 'Manager')
                    &&
                    <Table.Body>
                        {motoristas?.map((motorista) => (
                            <Table.Row key={motorista.id}>
                                <Table.Cell>
                                    <Button.Group>
                                        <TooltipInfo
                                            content="Editar motorista"
                                            trigger={
                                                <Button
                                                    color="blue"
                                                    onClick={() => {
                                                        setLoading(true);
                                                        navigate('/backoffice/motoristas/edit/' + motorista.id);
                                                    }}
                                                    icon="edit"
                                                />
                                            }
                                        />

                                        <TooltipInfo
                                            content="Ver programações do motorista"
                                            trigger={
                                                <Button
                                                    color="teal"
                                                    onClick={() => {
                                                        setSelectedDriverForProgrammings(motorista);
                                                        setProgrammingsModalOpen(true);
                                                    }}
                                                    icon="calendar alternate outline"
                                                />
                                            }
                                        />

                                        <TooltipInfo
                                            content="Excluir motorista"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => deleteMotoristaFunction(motorista)}
                                                    icon="trash"
                                                />
                                            }
                                        />

                                        <TooltipInfo
                                            content={motorista.isActive ? "Desativar motorista" : "Ativar motorista"}
                                            trigger={
                                                <Button
                                                    color={motorista.isActive ? 'red' : 'green'}
                                                    onClick={() => toggleActivationTruckDriver(motorista)}
                                                    icon={motorista.isActive ? 'power off' : 'power'}
                                                />
                                            }
                                        />

                                        {(sessionUser?.userData?.profile === 'Admin' || sessionUser?.userData?.profile === 'Manager') && (
                                            <TooltipInfo
                                                content="Redefinir senha (6 primeiros dígitos do CPF)"
                                                trigger={
                                                    <Button
                                                        color="orange"
                                                        onClick={() => resetMotoristaPasswordFunction(motorista)}
                                                        icon="key"
                                                    />
                                                }
                                            />
                                        )}
                                    </Button.Group>

                                </Table.Cell>
                                <Table.Cell>{`${motorista.firstName} ${motorista?.lastName ?? ''}`}</Table.Cell>
                                <Table.Cell>{StringUtils.formatCPF(motorista.cpf)}</Table.Cell>
                                <Table.Cell>{motorista?.clienteNome ?? '-'} </Table.Cell>
                                <Table.Cell>{motorista?.activeFleetName ?? '-'} </Table.Cell>
                                <Table.Cell>{motorista?.currentVehiclePlate ?? '-' } </Table.Cell>
                                <Table.Cell>{moment(motorista.createdAt).format('DD/MM/YY HH:mm')}</Table.Cell>
                                <Table.Cell>
                                    <Label key={motorista.id}
                                           color={!motorista.isActive ? 'red' : 'green'}>{motorista.isActive ? 'Sim' : 'Não'}</Label>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>}
            </Table>
            <Pagination totalPages={totalPages ?? 0}
                        defaultActivePage={filter?.Page ?? 1}
                        onPageChange={(e, {activePage}) => {
                            setFilter(prev => ({...prev, Page: activePage as number}))
                        }
                        }>
            </Pagination>

            {selectedDriverForProgrammings && (
                <DriverProgrammingsModal
                    open={programmingsModalOpen}
                    onClose={() => {
                        setProgrammingsModalOpen(false);
                        setSelectedDriverForProgrammings(null);
                    }}
                    driverId={selectedDriverForProgrammings.id}
                    driverName={`${selectedDriverForProgrammings.firstName} ${selectedDriverForProgrammings.lastName}`.trim()}
                />
            )}
        </div>
    );
};

export default TruckDriversList;
